import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check } from '../components/Icons.jsx';

const sets = [
  {
    title: 'Classic categorical',
    premises: [
      'All medical students study anatomy.',
      'Some medical students are mature students.',
    ],
    question: 'Do some mature students study anatomy?',
    answer: 'Yes — follows',
    explanation: 'There is a subset (mature medical students) that both studies anatomy and is a mature student. The conclusion "some mature students study anatomy" follows from the overlap between the two sets.',
    form: 'Some A are B. All B study C. → Some A study C. (Darii — valid)',
    trap: 'Students say "can\'t tell" because they worry about mature students who are NOT medical students. But the conclusion is about SOME mature students — and we know at least some mature students ARE medical students who DO study anatomy.',
  },
  {
    title: 'Invalid syllogism (affirming the consequent)',
    premises: [
      'All cardiologists attend the weekly MDT.',
      'Dr Singh attends the weekly MDT.',
    ],
    question: 'Is Dr Singh a cardiologist?',
    answer: 'No — does not follow',
    explanation: 'This is "affirming the consequent" — a classic logical fallacy. The major premise says all cardiologists attend MDT, but other specialties may also attend. Dr Singh attending MDT only tells us she meets the sufficient condition for many possibilities, not that she is definitely a cardiologist.',
    form: 'All A → B. X is B. → X is A? (Invalid — fallacy of affirming the consequent)',
    trap: 'Students accept this because it "feels true" in clinical context — cardiologists do attend MDTs. The logical form, not the content, determines validity.',
  },
  {
    title: 'Five-statement — GEM style',
    premises: [
      'Some graduate-entry applicants have a science degree.',
      'All applicants with a science degree studied biology.',
      'No biology student dislikes dissection.',
      'Some students who dislike dissection apply to dentistry.',
      'All dentistry applicants take the UCAT.',
    ],
    question: 'Do some graduate-entry applicants take the UCAT?',
    answer: 'Yes — follows',
    explanation: 'Trace the chain: Some GEM applicants → science degree (P1). Science degree → biology (P2). Biology → doesn\'t dislike dissection (P3, contrapositive of "dislike → not biology"). P4 and P5 are about a different group (those who dislike dissection). The key shortcut: GEM → science → biology → "no dissection dislike". This group can still take UCAT if they apply to dentistry or medicine. We don\'t need P4-P5 to answer the question — is there another route? Actually P1-P2-P3 establish a subset of GEM applicants exist and study biology; they could also be dentistry applicants (P5 → UCAT). So yes.',
    form: 'Chain syllogism across 5 premises. Focus on whether a valid chain connects the subject to the predicate.',
    trap: 'Five-statement sets intimidate students into "can\'t tell" when the chain is actually traceable. Teach them to start from the conclusion and work backwards.',
  },
  {
    title: 'Negative premises',
    premises: [
      'No surgeon avoids mortality and morbidity meetings.',
      'Some consultants are surgeons.',
    ],
    question: 'Do some consultants attend M&M meetings?',
    answer: 'Yes — follows',
    explanation: '"No surgeon avoids M&M" = all surgeons attend M&M. "Some consultants are surgeons" = there exists at least one consultant who is also a surgeon. That person attends M&M. So some consultants attend M&M.',
    form: 'No A avoids X = All A do X. Some B are A. → Some B do X.',
    trap: 'The double-negative phrasing ("no surgeon avoids") confuses students into thinking a negative conclusion follows. Convert the negative to a positive first: "All surgeons attend M&M."',
  },
  {
    title: 'Exclusive conclusion',
    premises: [
      'All UCAT scores above 2900 are competitive.',
      'All competitive scores receive interview invitations.',
      'Asha scored 2750.',
    ],
    question: 'Does Asha receive an interview invitation?',
    answer: 'No — does not follow (cannot be confirmed)',
    explanation: 'Asha scored 2750, which is below the threshold stated for "competitive". The chain (above 2900 → competitive → interview) does not apply to Asha. However, the premises only say what happens if you score above 2900 — they say nothing about whether lower scores can ALSO receive invitations by other routes. Strictly: "Does not follow from these premises" — we can\'t say definitively she won\'t get one.',
    form: 'Denying the antecedent. "If A then B. Not A." → "Not B" does not follow.',
    trap: 'Students conclude "Asha does not get an interview" — but that is denying the antecedent. The correct answer is "cannot confirm" or "does not follow". If the UCAT question asked whether she IS competitive: False. If asked whether she gets an interview: Can\'t tell.',
  },
];

export default function TutorSyllogismBoard() {
  const [idx, setIdx] = useState(0);
  const [revealedPremises, setRevealedPremises] = useState(0);
  const [showQuestion, setShowQuestion] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const s = sets[idx];

  const next = () => {
    setIdx((i) => (i + 1) % sets.length);
    setRevealedPremises(0);
    setShowQuestion(false);
    setShowAnswer(false);
    setShowForm(false);
  };

  const revealNext = () => {
    if (revealedPremises < s.premises.length) {
      setRevealedPremises((n) => n + 1);
    } else {
      setShowQuestion(true);
    }
  };

  return (
    <>
      <Link to="/tutor-portal" className="back-link"><ArrowLeft width="16" height="16" />Back to tutor portal</Link>
      <div className="page-head">
        <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>Tutor Portal · Syllogisms Whiteboard</div>
        <h1>Reveal premises one at a time.</h1>
        <p className="subtitle">Project each premise in sequence. Students note their running inference. The question — and then the answer — stay hidden until you're ready. Teaches the step-by-step reasoning the UCAT demands.</p>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 18 }}>
        <span style={{ padding: '4px 12px', borderRadius: 999, background: '#f3eefc', color: '#5b21b6', fontWeight: 700, fontSize: 12 }}>{s.title}</span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Set {idx + 1} of {sets.length}</span>
        <button onClick={next} className="btn btn-primary" style={{ marginLeft: 'auto' }}>Next set <ArrowRight width="14" height="14" /></button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 }}>
        {s.premises.map((premise, i) => (
          <div key={i} style={{
            padding: '16px 20px', borderRadius: 12, fontSize: 15, lineHeight: 1.6,
            border: `1px solid ${i < revealedPremises ? 'var(--border)' : '#d8d6e6'}`,
            background: i < revealedPremises ? '#fff' : '#f9f7ff',
            color: i < revealedPremises ? 'var(--text)' : 'var(--text-muted)',
            transition: 'all 0.2s',
          }}>
            {i < revealedPremises ? (
              <>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 10 }}>P{i + 1}</span>
                <strong>{premise}</strong>
              </>
            ) : (
              <span style={{ fontStyle: 'italic' }}>Premise {i + 1} — hidden</span>
            )}
          </div>
        ))}
      </div>

      {!showQuestion && (
        <button onClick={revealNext} className="btn btn-primary" style={{ marginBottom: 16 }}>
          {revealedPremises < s.premises.length ? `Reveal premise ${revealedPremises + 1}` : 'Show question'} <ArrowRight width="14" height="14" />
        </button>
      )}

      {showQuestion && !showAnswer && (
        <div className="card" style={{ marginBottom: 16, padding: '20px 24px', background: 'var(--card-soft)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Question — ask students now</div>
          <p style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{s.question}</p>
          <button onClick={() => setShowAnswer(true)} className="btn btn-primary">
            <Check width="14" height="14" /> Reveal answer
          </button>
        </div>
      )}

      {showAnswer && (
        <>
          <div className="card" style={{ marginBottom: 14, padding: '18px 22px', borderLeft: '4px solid var(--live-green)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Answer</div>
            <p style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{s.answer}</p>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: 'var(--text-2)' }}>{s.explanation}</p>
          </div>

          {!showForm ? (
            <button onClick={() => setShowForm(true)} className="btn" style={{ marginBottom: 14 }}>
              Show logical form + student trap
            </button>
          ) : (
            <div className="card" style={{ marginBottom: 16, padding: '18px 22px', background: '#fffbeb', borderLeft: '4px solid #f59e0b' }}>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Logical form</div>
                <code style={{ fontSize: 13, color: '#1c1917', background: '#fef9c3', padding: '6px 10px', borderRadius: 6, display: 'block', lineHeight: 1.55 }}>{s.form}</code>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Student trap</div>
                <p style={{ margin: 0, fontSize: 13.5, color: '#78350f', lineHeight: 1.55 }}>{s.trap}</p>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
